import moment from 'moment'
import { useState } from 'react'

import { Transaction } from '@/reducers/admin/allTransactions.slice'
import {
  selectFinancialAccountById,
  selectIsUserManualAccount,
} from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { Table, Text, DocumentUploadDialog } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { DATE_FORMATS } from '@/utils/dateHelpers'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { EditableTransactionTypeLabel } from '@/components/EditableTransactionTypeLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { TransactionCategoryLabel } from '@/components/TransactionCategoryLabel'
import { formatAccountName } from '../helpers'
import {
  selectIsLockedTransaction,
  selectIsSplitTransaction,
} from '../transactions.selectors'

import { TransactionDetailsMenu } from './TransactionDetailsMenu'
import { CellContent } from './CellContent'
import { DisplayRowIcon } from './DisplayRowIcon'
import { SplitTransactionRow } from './SplitTransactionRow'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { UploadDocumentType } from '@/constants/businessConstants'

interface TransactionsTableRowProps {
  transaction: Transaction
  hasError?: boolean
}

export const TransactionsTableRow = ({
  transaction,
  hasError,
}: TransactionsTableRowProps) => {
  const {
    id,
    splitTransactions,
    amountInCents,
    requestedClarificationAt,
    date,
    description,
    nickname,
    autocategorizedAt,
    financialAccountId,
  } = transaction
  const isSplitTransaction = useReselector(
    selectIsSplitTransaction,
    transaction.id
  )
  const account = useReselector(
    selectFinancialAccountById,
    Number(financialAccountId)
  )
  const locked = useReselector(
    selectIsLockedTransaction,
    transaction.splitFrom ?? transaction.id
  )

  const isManualTransaction = useReselector(
    selectIsUserManualAccount,
    financialAccountId
  )

  const [fileModalOpen, setFileModalOpen] = useState(false)

  const { uploadDocuments } = useDocumentUpload({
    documentType: UploadDocumentType.RECEIPT,
    extraPayload: { transactionId: transaction.id },
  })

  return (
    <>
      <Table.Row
        className={`${
          locked
            ? 'bg-stone-100 text-gray-500 opacity-50 cursor-not-allowed pointer-events-none'
            : ''
        } ${
          requestedClarificationAt
            ? hasError
              ? 'bg-red-25'
              : 'bg-yellow-25'
            : ''
        } text-neutral-600`}
      >
        {/* ICON */}
        <Table.Cell>
          <CellContent locked={locked}>
            <DisplayRowIcon
              isLocked={locked}
              isAutoCategorized={Boolean(autocategorizedAt)}
              isManualTransaction={isManualTransaction}
            />
          </CellContent>
        </Table.Cell>

        {/* DATE */}
        <Table.Cell>
          <CellContent locked={locked}>
            <Text variant={TypographyStyles.BODY_SM}>
              {moment.utc(date).format(DATE_FORMATS.DISPLAY_SIMPLE)}
            </Text>
          </CellContent>
        </Table.Cell>

        {/* DESCRIPTION */}
        <Table.Cell className="max-w-0">
          <CellContent locked={locked}>
            {nickname && (
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.MUTED}
                className="truncate"
                title={nickname}
              >
                <i>{nickname}</i>
              </Text>
            )}
            <Text
              variant={TypographyStyles.BODY_SM}
              className="truncate"
              title={description}
            >
              {description}
            </Text>
          </CellContent>
        </Table.Cell>

        {/* AMOUNT */}
        <Table.Cell
          className={`text-right ${amountInCents > 0 ? 'text-green-600' : ''}`}
        >
          <CellContent locked={locked}>
            <Text variant={TypographyStyles.BODY_SM}>
              {isSplitTransaction && (
                <s>
                  <CurrencyFormatLabel value={centsToDollars(amountInCents)} />
                </s>
              )}
              {!isSplitTransaction && (
                <CurrencyFormatLabel value={centsToDollars(amountInCents)} />
              )}
            </Text>
          </CellContent>
        </Table.Cell>

        {/* TYPE */}
        <Table.Cell
          className={`text-center ${hasError ? '[&_button]:!border-red-200' : ''}`}
        >
          <CellContent locked={locked}>
            {!isSplitTransaction && (
              <EditableTransactionTypeLabel transaction={transaction} />
            )}
            {isSplitTransaction && (
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Split Transaction
              </Text>
            )}
          </CellContent>
        </Table.Cell>

        {/* CATEGORY */}
        <Table.Cell
          className={`overflow-hidden max-w-0 ${hasError ? '[&_button]:!border-red-200' : ''}`}
        >
          <CellContent locked={locked}>
            {!isSplitTransaction && (
              <div className="truncate">
                <TransactionCategoryLabel
                  variant="text"
                  editable
                  transaction={transaction}
                  filtered
                  useRecategorizationModal
                />
              </div>
            )}
          </CellContent>
        </Table.Cell>

        {/* ACCOUNT */}
        <Table.Cell className="max-w-0">
          <CellContent locked={locked}>
            {account?.plaidInstitutionName && (
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.MUTED}
                className="truncate"
                title={account.plaidInstitutionName}
              >
                {account.plaidInstitutionName}
              </Text>
            )}
            <span
              className="flex items-baseline text-sm"
              title={formatAccountName(account)}
            >
              <span className="truncate">{account?.name}</span>
              {account?.mask && (
                <span className="shrink-0"> {account.mask}</span>
              )}
              {account?.inactive && (
                <span className="shrink-0"> (inactive)</span>
              )}
            </span>
          </CellContent>
        </Table.Cell>

        {/* ACTION MENU */}
        <Table.Cell>
          <CellContent locked={locked}>
            {!isSplitTransaction && !locked && (
              <TransactionDetailsMenu
                transaction={transaction}
                toggleFileModal={() => setFileModalOpen(true)}
                isManualTransaction={isManualTransaction}
              />
            )}
          </CellContent>
        </Table.Cell>
      </Table.Row>

      {isSplitTransaction &&
        splitTransactions?.map((splitT, index) => (
          <SplitTransactionRow
            key={splitT.id}
            splitTransaction={splitT}
            locked={locked}
            isLastSplit={index === splitTransactions.length - 1}
            parentId={id}
            isManualTransaction={isManualTransaction}
          />
        ))}

      <DocumentUploadDialog
        isOpen={fileModalOpen}
        onOpenChange={setFileModalOpen}
        onClose={() => setFileModalOpen(false)}
        onUpload={(files) => {
          uploadDocuments(files)
        }}
        cardTitle="Upload Receipt"
        cardDescription="Upload a receipt or document for this transaction"
      />
    </>
  )
}
